<template>
    <div class="cell" :class="classes">
        <div class="content">
            <slot></slot>
        </div>
    </div>
</template>

<script lang="ts">
    import {ContractCaptions, DeploymentState} from '@cquential/api';
    import {SicknessInformation, UnavailabilityStatus} from '@/components/types';
    import {Component, Prop, Vue} from 'vue-facing-decorator';

    @Component
    export default class PlanbordGridCell extends Vue {
        @Prop({type: Boolean}) public header!: boolean;
        @Prop({type: Boolean}) public balance!: boolean;
        @Prop({type: Boolean}) public info!: boolean;
        @Prop({type: Boolean}) public centeredHorizontally!: boolean;
        @Prop({type: Boolean}) public centeredVertically!: boolean;
        @Prop({type: Boolean}) public noPadding!: boolean;
        @Prop({type: Boolean}) public weekend!: boolean;
        @Prop({type: Boolean}) public hasWish!: boolean;
        @Prop({type: Boolean}) public hasLeaveProblems!: boolean;
        @Prop({type: Boolean}) public holiday!: boolean;
        @Prop({type: Boolean}) public isStampPeriodStart!: boolean;
        @Prop({type: Boolean}) public isInStampPeriod!: boolean;
        @Prop({type: Boolean}) public hasStamp!: boolean;
        @Prop({type: Boolean}) public isIterationStart!: boolean;
        @Prop({type: Boolean}) public isIterationEnd!: boolean;
        @Prop({type: Boolean}) public isStampStart!: boolean;
        @Prop({type: Boolean}) public isStampEnd!: boolean;
        @Prop({type: Boolean}) public isPublished!: boolean;
        @Prop({type: Boolean}) public inConstruction!: boolean;
        @Prop({type: Boolean}) public isPublishedEndDate!: boolean;
        @Prop({type: Boolean}) public isProposedEndDate!: boolean;
        @Prop({type: Boolean}) public isProposed!: boolean;
        @Prop({type: Boolean}) public isWishInputEndDate!: boolean;
        @Prop({type: Boolean}) public isNotEmployed!: boolean;
        @Prop({type: Boolean}) public hasLeavePeriodsOrForm!: boolean;
        @Prop({type: Boolean}) public rosterEnded!: boolean;
        @Prop({type: Boolean}) public publishedWithoutRights!: boolean;
        @Prop() public shiftStateDeviations?: DeploymentState[];
        @Prop() public contractDeviations?: (keyof ContractCaptions)[];
        @Prop() public sicknessInformation?: SicknessInformation;
        @Prop() public unavailabilityStatus?: UnavailabilityStatus;
        @Prop({type: String}) public workloadScheduleFulfillmentStatus?: 'full' | 'partial' | 'none';

        public get classes(): Record<string, boolean> {
            const classes: Record<string, boolean> = {
                'header': this.header,
                'balance': this.balance,
                'info': this.info,
                'centeredHorizontally': this.centeredHorizontally,
                'centeredVertically': this.centeredVertically,
                'noPadding': this.noPadding,
                'weekend': this.weekend,
                'wish': this.hasWish,
                'leave-problems': this.hasLeaveProblems,
                'holiday': this.holiday,
                'stamp-period-start': this.isStampPeriodStart,
                'stamp-period': this.isInStampPeriod,
                'has-stamp': this.hasStamp,
                'iteration-start': this.isIterationStart,
                'iteration-end': this.isIterationEnd,
                'stamp-start': this.isStampStart,
                'stamp-end': this.isStampEnd,
                'published': this.isPublished,
                'inConstruction': this.inConstruction,
                'published-end': this.isPublishedEndDate,
                'proposed-end': this.isProposedEndDate,
                'proposed': this.isProposed,
                'wish-input-end': this.isWishInputEndDate,
                'is-not-employed': this.isNotEmployed,
                'has-contract-alternative-times': !!this.contractDeviations,
                'has-leave-periods-or-form': this.hasLeavePeriodsOrForm,
                'roster-ended': this.rosterEnded,
                'published-no-rights': this.publishedWithoutRights,
            };

            if (this.shiftStateDeviations) {
                for (const state of this.shiftStateDeviations) {
                    classes['shift-warning-' + state] = true;
                }
            }

            if (this.contractDeviations) {
                for (const key of this.contractDeviations) {
                    classes['contract-warning-' + key] = true;
                }
            }

            if (this.sicknessInformation) {
                if (this.sicknessInformation.isSick) {
                    if (this.sicknessInformation.isFullySick) {
                        classes['is-completely-sick'] = true;
                    } else {
                        classes['is-partly-sick'] = true;
                    }
                }

                if (this.sicknessInformation.hasExpectedRecovery) {
                    classes['sickness-expected-recovery'] = true;
                }
            }

            if (this.unavailabilityStatus && this.unavailabilityStatus.hasUnavailabilities) {
                if (this.unavailabilityStatus.isEntireDay) {
                    classes['unavailable-all-day'] = true;
                } else {
                    classes['unavailable-partly'] = true;
                }
            }

            if (this.workloadScheduleFulfillmentStatus) {
                classes[`work-schedule-status-${this.workloadScheduleFulfillmentStatus}`] = true;
            }

            return classes;
        }
    }
</script>

<style lang="scss" scoped>
    .cell {
        border-right: 1px solid var(--cell-border-color);
        border-bottom: 1px solid var(--cell-border-color);
        background: var(--cell-background-color);
        transition: background-color .2s ease;
        overflow-wrap: break-word;

        &:not(.noPadding) {
            padding: var(--cell-padding);
        }

        &.info {
            background: transparent;
        }

        &.clickable {
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        &.header {
            background: var(--header-cell-background-color);
            color: var(--header-text-color);
            padding: 0.3125rem;
            font-weight: bold;

            &.published {
                border-bottom: 0.375rem solid var(--published-header-color);
                padding-bottom: 0.125rem;
            }

            &.published-no-rights {
                border-bottom: 0.375rem solid var(--published-header-color);
                padding-bottom: 0.125rem;
                background: var(--published-without-rights-gradient-header-background) no-repeat;
            }

            &.proposed:not(.published) {
                border-bottom: 0.375rem solid var(--construction-header-color);
                padding-bottom: 0.125rem;
                background: var(--proposed-gradient-header-background) no-repeat;
            }

            &.inConstruction {
                border-bottom: 0.375rem solid var(--construction-header-color);
                padding-bottom: 0.125rem;
            }

            &.ended {
                border-bottom: 0.375rem solid var(--ended-header-color);
                padding-bottom: 0.125rem;
            }
        }

        &.balance {
            text-align: right;
        }

        .content {
            min-height: 0.75rem;
            height: 100%;
        }

        &.proposed.stamp-period:not(.has-stamp) {
            background-color: var(--stamp-during-proposed-roster-background);
        }

        &.has-stamp {
            background-color: var(--stamp-background);
            border-bottom: 1px solid #666;

            &.iteration-end {
                border-right: 0.375rem double var(--stamp-during-proposed-roster-background);
            }

            &.stamp-end {
                border-right: 4px solid var(--stamp-end-color);
            }
        }

        &.weekend {
            background-color: var(--holiday-cell-background);
        }

        &.holiday {
            background-color: var(--holiday-cell-background);
        }

        &.wish {
            background: var(--wish-cell-background);
        }

        &.shift-warning-free, &.shift-warning-unpaid_leave, &.shift-warning-partime_leave, &.shift-warning-comp_leave,
        &.has-leave-periods-or-form, &.is-partly-sick {
            background: var(--leave-cell-background);

            &.has-stamp {
                background: var(--leave-stamp-background);
            }
        }

        &.shift-warning-sick,
        &.shift-warning-leave_during_sickness,
        &.contract-warning-sick,
        &.is-completely-sick {
            background: var(--sick-cell-background);

            &.has-stamp {
                background: var(--sick-stamp-background);
            }
        }

        &.sickness-expected-recovery {
            background: var(--expected-recovery-background);

            &.has-stamp {
                background: var(--expected-recovery-stamp-background);
            }
        }

        // For anything starting with special_leave, so also covers special_leave2
        &.shift-warning-special_leave, &.shift-warning-special_leave2 {
            background: var(--special-leave-background);

            &.has-stamp {
                background: var(--special-leave-stamp-background);
            }
        }

        &.shift-warning-care_leave {
            background: var(--care-leave-background);

            &.has-stamp {
                background: var(--care-leave-stamp-background);
            }
        }

        &.shift-warning-comp_leave {
            background: var(--compensation-leave-background);

            &.has-stamp {
                background: var(--compensation-leave-stamp-background);
            }
        }

        &.contract-warning-pregnant {
            background: var(--maternity-background);

            &.has-stamp {
                background: var(--maternity-stamp-background);
            }
        }

        &.contract-warning-parentalLeave {
            background: var(--parental-leave-background);

            &.has-stamp {
                background: var(--parental-leave-stamp-background);
            }
        }

        &.contract-warning-other {
            background: var(--other-background);

            &.has-stamp {
                background: var(--other-stamp-background);
            }
        }

        &.unavailable-partly {
            background: var(--absent-but-possibly-available-background);
        }

        &.unavailable-all-day {
            background: var(--absent-and-unavailable-background);
        }

        &.temp-agency {
            background-color: var(--temp-agency-background);
        }

        &.is-not-employed {
            background: var(--unemployed-cell-background);
        }

        &.rosterEnded {
            background: var(--roster-ended-cell-background);
            opacity: .5;
            pointer-events: none;
        }

        &.proposed-end, &.published-end, &.stamp-period-start, &.wish-input-end {
            border-left: 0.625rem double var(--roster-phase-border-color);
        }

        &.selected, &.selected.has-stamp, &.weekend.selected {
            background: var(--cell-highlight-background);
            box-shadow: inset 0 -1px 0 0 var(--cell-highlight-shadow-color);
            transition: box-shadow var(--theme-generic-transition);
        }

        &.active, &.active.has-stamp, &.weekend.active {
            background: var(--cell-highlight-background);
            box-shadow: inset 0 -4px 0 0 var(--cell-highlight-shadow-color);
            transition: none;
        }

        &.work-schedule-status-full {
            border-top: 0.375rem solid var(--work-schedule-status-full);
            padding-top: 0.125rem;
        }

        &.work-schedule-status-partial {
            border-top: 0.375rem solid var(--work-schedule-status-partial);
            padding-top: 0.125rem;
        }

        &.work-schedule-status-none {
            border-top: 0.375rem solid var(--work-schedule-status-none);
            padding-top: 0.125rem;
        }

        &.copied {
            outline: 1px dashed #000;
        }

        &.centeredHorizontally, &.centeredVertically {
            .content {
                display: flex;
                height: 100%;
                min-height: 1rem;
            }

            &.balance {
                .content {
                    justify-content: end;
                }
            }
        }

        &.centeredHorizontally {
            &, &.balance {
                .content {
                    justify-content: center;
                }
            }
        }

        &.centeredVertically {
            .content {
                align-items: center;
            }
        }
    }
</style>
